/**
 * 파트너 상세의 네 절 — 「화면 설계 › 파트너 상세」 ①~④.
 * @spec:2026-10-14-phai-admin-외부연동키-관리화면
 *
 * 순수 UI — 데이터와 이벤트는 페이지(Detail.tsx)가 넘기고 받는다.
 */
import React, { useState } from "react";
import Button from "components/atoms/Button";
import type {
  ApiKeyView,
  PartnerEventView,
  PartnerMeta,
  PartnerSummary,
  WebhookSecretView,
} from "api/partnerAdminService";
import {
  DB_SECRET_SOURCE_LABEL,
  actionLabel,
  codeLabel,
  consentProfileLabel,
  formatDateTime,
  summarizeChanges,
} from "utils/partnerAdmin";
import { SecretSourceText } from "./PartnerTable";
import {
  ButtonRow,
  EmptyText,
  Guide,
  InfoGrid,
  Mono,
  Muted,
  Notice,
  Section,
  SectionHeader,
  SectionTitle,
  StatusText,
  Table,
  Td,
  Th,
  Tr,
} from "./style";

// ---------------------------------------------------------------------------
// ① 기본 정보
// ---------------------------------------------------------------------------

interface PartnerInfoSectionProps {
  partner: PartnerSummary;
  meta?: PartnerMeta;
  onEdit: () => void;
  onToggleActive: () => void;
}

export const PartnerInfoSection: React.FC<PartnerInfoSectionProps> = ({ partner, meta, onEdit, onToggleActive }) => (
  <Section data-testid="partner-info">
    <SectionHeader>
      <SectionTitle>기본 정보</SectionTitle>
      <ButtonRow>
        <Button
          type="Text"
          size="Small"
          variant="Outlined"
          color="Secondary"
          onClick={onEdit}
          disabled={!meta}
          data-testid="partner-edit"
        >
          수정
        </Button>
        <Button
          type="Text"
          size="Small"
          variant="Outlined"
          color={partner.isActive ? "Negative" : "Primary"}
          onClick={onToggleActive}
          data-testid="partner-toggle-active"
        >
          {partner.isActive ? "비활성" : "활성"}
        </Button>
      </ButtonRow>
    </SectionHeader>
    <InfoGrid>
      <dt>이름</dt>
      <dd>{partner.name}</dd>
      <dt>종류</dt>
      <dd>{codeLabel(partner.code, meta)}</dd>
      <dt>연결 계정</dt>
      <dd>{partner.userEmail ?? partner.userId}</dd>
      <dt>동의 화면 프로필</dt>
      <dd>{consentProfileLabel(partner.consentProfile, meta)}</dd>
      <dt>콜백 허용 도메인</dt>
      <dd>{partner.callbackDomains.length > 0 ? partner.callbackDomains.join(", ") : <Muted>없음</Muted>}</dd>
      <dt>자동 전달 주소</dt>
      <dd>{partner.provisionUrl ?? <Muted>없음</Muted>}</dd>
      <dt>수집 후 자동 분석</dt>
      <dd>{partner.autoReviewOnCollect ? "사용" : "사용 안 함"}</dd>
      <dt>상태</dt>
      <dd>
        <StatusText $on={partner.isActive} data-testid="partner-status">
          {partner.isActive ? "활성" : "비활성"}
        </StatusText>
        {!partner.isActive && <Muted> · 새 요청을 받지 않습니다</Muted>}
      </dd>
    </InfoGrid>
  </Section>
);

// ---------------------------------------------------------------------------
// ② 연동 키
// ---------------------------------------------------------------------------

interface ApiKeySectionProps {
  keys: ApiKeyView[];
  onIssue: () => void;
  onEdit: (key: ApiKeyView) => void;
  onRevoke: (key: ApiKeyView) => void;
  /** [원문 복사] — 원문이 보관된(rawAvailable) 키에서만 */
  onReveal: (key: ApiKeyView) => void;
  /** [원문 등록] — 원문이 없거나 복호화·불일치로 복구가 필요한 키에서 */
  onRegisterRaw: (key: ApiKeyView) => void;
  /** meta 암호화 준비 여부 — 아니면 [원문 등록]을 막는다(서명 키 버튼과 같은 규칙) */
  encryptionReady: boolean | undefined;
  /** reveal 요청 중인 키 — 그 행의 [원문 복사]를 비활성화해 연타를 막는다 */
  revealingKeyId: number | null;
  /** 복호화·불일치로 [원문 등록]을 보조로 열어 둘 키들 */
  recoverKeyIds: ReadonlySet<number>;
}

interface KeyRowActions {
  onEdit: (key: ApiKeyView) => void;
  onRevoke: (key: ApiKeyView) => void;
  onReveal: (key: ApiKeyView) => void;
  onRegisterRaw: (key: ApiKeyView) => void;
  encryptionReady: boolean | undefined;
  revealingKeyId: number | null;
  recoverKeyIds: ReadonlySet<number>;
}

const KeyRow: React.FC<{ apiKey: ApiKeyView } & KeyRowActions> = ({
  apiKey: k,
  onEdit,
  onRevoke,
  onReveal,
  onRegisterRaw,
  encryptionReady,
  revealingKeyId,
  recoverKeyIds,
}) => {
  const revoked = !!k.revokedAt;
  // 버튼 노출(AC-F1~F4): undefined(옛 서버)면 둘 다 숨김. 복호화·불일치면 [원문 등록]을 보조로 함께 연다.
  const needsRecover = recoverKeyIds.has(k.id);
  const showReveal = !revoked && k.rawAvailable === true;
  const showRegister = !revoked && (k.rawAvailable === false || needsRecover);
  return (
    <Tr $muted={revoked} data-testid={`api-key-row-${k.id}`}>
      <Td>{k.label ?? <Muted>이름 없음</Muted>}</Td>
      <Td>
        <Mono>{k.fingerprint}</Mono>
      </Td>
      <Td>
        <Mono>{k.ipAllowlist.length > 0 ? k.ipAllowlist.join(", ") : "없음(전체 거부)"}</Mono>
      </Td>
      <Td>
        {formatDateTime(k.createdAt)}
        {k.createdBy && <Muted> · {k.createdBy}</Muted>}
      </Td>
      <Td>{formatDateTime(k.lastUsedAt, "사용 전")}</Td>
      <Td data-testid={`api-key-status-${k.id}`}>
        {revoked ? (
          <span>
            폐기됨 · {formatDateTime(k.revokedAt)}
            {k.revokedBy && ` · ${k.revokedBy}`}
          </span>
        ) : (
          <StatusText $on>사용 중</StatusText>
        )}
      </Td>
      <Td>
        {!revoked && (
          <ButtonRow>
            {showReveal && (
              <Button
                type="Text"
                size="XSmall"
                variant="Outlined"
                color="Primary"
                onClick={() => onReveal(k)}
                disabled={revealingKeyId === k.id}
                data-testid={`api-key-reveal-${k.id}`}
              >
                원문 복사
              </Button>
            )}
            {showRegister && (
              <Button
                type="Text"
                size="XSmall"
                variant="Outlined"
                color="Secondary"
                onClick={() => onRegisterRaw(k)}
                disabled={encryptionReady !== true}
                data-testid={`api-key-register-raw-${k.id}`}
              >
                원문 등록
              </Button>
            )}
            <Button
              type="Text"
              size="XSmall"
              variant="Outlined"
              color="Secondary"
              onClick={() => onEdit(k)}
              data-testid={`api-key-edit-${k.id}`}
            >
              수정
            </Button>
            <Button
              type="Text"
              size="XSmall"
              variant="Outlined"
              color="Negative"
              onClick={() => onRevoke(k)}
              data-testid={`api-key-revoke-${k.id}`}
            >
              폐기
            </Button>
          </ButtonRow>
        )}
      </Td>
    </Tr>
  );
};

const KeyTable: React.FC<{ keys: ApiKeyView[]; testId: string } & KeyRowActions> = ({ keys, testId, ...actions }) => (
  <Table data-testid={testId}>
    <thead>
      <tr>
        <Th>이름</Th>
        <Th>지문</Th>
        <Th>허용 IP</Th>
        <Th>만든 날 · 만든 사람</Th>
        <Th>마지막 사용</Th>
        <Th>상태</Th>
        <Th />
      </tr>
    </thead>
    <tbody>
      {keys.map((k) => (
        <KeyRow key={k.id} apiKey={k} {...actions} />
      ))}
    </tbody>
  </Table>
);

export const ApiKeySection: React.FC<ApiKeySectionProps> = ({
  keys,
  onIssue,
  onEdit,
  onRevoke,
  onReveal,
  onRegisterRaw,
  encryptionReady,
  revealingKeyId,
  recoverKeyIds,
}) => {
  const [showRevoked, setShowRevoked] = useState(false);
  const active = keys.filter((k) => !k.revokedAt);
  const revoked = keys.filter((k) => !!k.revokedAt);
  const actions: KeyRowActions = { onEdit, onRevoke, onReveal, onRegisterRaw, encryptionReady, revealingKeyId, recoverKeyIds };

  return (
    <Section data-testid="api-key-section">
      <SectionHeader>
        <SectionTitle>연동 키</SectionTitle>
        <Button type="Text" size="Small" variant="Contained" color="Primary" onClick={onIssue} data-testid="api-key-issue">
          키 발급
        </Button>
      </SectionHeader>

      {active.length > 0 ? (
        <KeyTable keys={active} {...actions} testId="api-key-table" />
      ) : (
        <EmptyText>사용 중인 키가 없습니다.</EmptyText>
      )}

      {revoked.length > 0 && (
        <div>
          <Button
            type="Text"
            size="XSmall"
            variant="Ghost"
            color="Secondary"
            onClick={() => setShowRevoked((v) => !v)}
            data-testid="api-key-show-revoked"
          >
            {showRevoked ? "폐기된 키 접기" : `폐기된 키 보기 (${revoked.length})`}
          </Button>
          {showRevoked && <KeyTable keys={revoked} {...actions} testId="api-key-revoked-table" />}
        </div>
      )}

      <Guide data-testid="api-key-rotation-guide">
        <li>새 키를 발급합니다. 옛 키는 그대로 둡니다.</li>
        <li>상대가 새 키로 바꿉니다(자동 전달이면 바로 바뀝니다).</li>
        <li>옛 키의 「마지막 사용」이 더 이상 바뀌지 않는지 확인합니다.</li>
        <li>옛 키를 폐기합니다.</li>
      </Guide>
    </Section>
  );
};

// ---------------------------------------------------------------------------
// ③ 웹훅 서명 키
// ---------------------------------------------------------------------------

export const ENCRYPTION_NOT_READY_TEXT = "암호화 키가 설정되지 않아 저장할 수 없습니다";

interface WebhookSecretSectionProps {
  secret: WebhookSecretView;
  /** meta를 아직 못 받았으면 undefined — 그동안은 버튼만 막고 안내는 띄우지 않는다 */
  encryptionReady: boolean | undefined;
  onInput: () => void;
  onGenerate: () => void;
  onClear: () => void;
}

export const WebhookSecretSection: React.FC<WebhookSecretSectionProps> = ({
  secret,
  encryptionReady,
  onInput,
  onGenerate,
  onClear,
}) => (
  <Section data-testid="webhook-secret-section">
    <SectionHeader>
      <SectionTitle>웹훅 서명 키</SectionTitle>
      <ButtonRow>
        <Button
          type="Text"
          size="Small"
          variant="Outlined"
          color="Secondary"
          onClick={onInput}
          disabled={encryptionReady !== true}
          data-testid="webhook-secret-input-open"
        >
          값 입력
        </Button>
        <Button
          type="Text"
          size="Small"
          variant="Outlined"
          color="Primary"
          onClick={onGenerate}
          disabled={encryptionReady !== true}
          data-testid="webhook-secret-generate-open"
        >
          새로 생성
        </Button>
        <Button
          type="Text"
          size="Small"
          variant="Outlined"
          color="Negative"
          onClick={onClear}
          disabled={secret.source !== "db"}
          data-testid="webhook-secret-clear"
        >
          DB 값 지우기
        </Button>
      </ButtonRow>
    </SectionHeader>
    {encryptionReady === false && (
      <Notice $tone="warn" data-testid="webhook-secret-encryption-off">
        {ENCRYPTION_NOT_READY_TEXT}
      </Notice>
    )}
    <InfoGrid>
      <dt>출처</dt>
      <dd data-testid="webhook-secret-source">
        <SecretSourceText secret={secret} />
      </dd>
      {secret.source === "db" && (
        <>
          <dt>DB 값</dt>
          <dd>{(secret.dbSource && DB_SECRET_SOURCE_LABEL[secret.dbSource]) ?? "—"}</dd>
          <dt>설정 일시</dt>
          <dd>{formatDateTime(secret.setAt)}</dd>
        </>
      )}
    </InfoGrid>
  </Section>
);

// ---------------------------------------------------------------------------
// ④ 변경 이력
// ---------------------------------------------------------------------------

export const EventHistory: React.FC<{ events: PartnerEventView[]; keys: ApiKeyView[] }> = ({ events, keys }) => {
  const keyName = (id: number | null) => {
    if (id == null) return null;
    const k = keys.find((x) => x.id === id);
    return k ? `${k.label ?? "이름 없음"} (${k.fingerprint})` : `키 #${id}`;
  };
  // 최근 순 — 계약이 순서를 정하지 않아 화면에서 정렬한다.
  const sorted = [...events].sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.id - a.id);

  return (
    <Section data-testid="partner-events">
      <SectionHeader>
        <SectionTitle>변경 이력</SectionTitle>
      </SectionHeader>
      {sorted.length === 0 ? (
        <EmptyText>이력이 없습니다.</EmptyText>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>일시</Th>
              <Th>한 사람</Th>
              <Th>한 일</Th>
              <Th>바뀐 내용</Th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((ev) => {
              const target = keyName(ev.apiKeyId);
              const summary = summarizeChanges(ev.changes);
              return (
                <Tr key={ev.id} data-testid={`partner-event-${ev.id}`}>
                  <Td style={{ whiteSpace: "nowrap" }}>{formatDateTime(ev.createdAt)}</Td>
                  <Td>{ev.actorEmail ?? <Muted>알 수 없음</Muted>}</Td>
                  <Td style={{ whiteSpace: "nowrap" }}>{actionLabel(ev.action)}</Td>
                  <Td>
                    {target && <Muted>{target} </Muted>}
                    {summary}
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Section>
  );
};
